import { Logger } from "@/lib/logger/Logger";
import { AxiosError } from "axios";
import { supabase } from "@/lib/supabase";
import { v4 as uuid } from "uuid";
import {
	PatientEntityType,
	SupabaseTableEnum,
} from "@/lib/supabase/supabaseTypes";
import { CompleteJobDocumentInformation } from "../actions/useExportActions";
import { formatDocumentPropsForApi, postDataToApi } from "./export-xml-helpers";
import { JobDocumentTypeEnum } from "../../pages/job-page/job-document/job-document.types";
import { useCentralStore } from "../../store/Central";
import { StorageBucketsEnum } from "../../types/enums";
import { removeStringTag } from "../../lib/utils/utils-functions";
import {
	buildSumexSupportMessage,
	extractServiceErrorDetail,
	mapSumexError,
} from "./sumex-error-messages";
import { resolveSumexXmlVersion } from "./sumex-xml-version";
import { PreflightProblem, problemForWireField } from "./sumex-preflight";
import { createFileMetadata } from "@/lib/utils/file-utils";
import { XmlTemplateType } from "../../pdf-templates/document-template.types";

const getXmlTemplateType = (
	documentType: JobDocumentTypeEnum | null
): XmlTemplateType | null => {
	if (documentType === JobDocumentTypeEnum.Quotation) {
		return XmlTemplateType.KOSTENVORANSCHLAG_XML;
	}
	if (documentType === JobDocumentTypeEnum.DeliveryNote) {
		return XmlTemplateType.LIEFERSCHEIN_XML;
	}
	return null;
};

export enum XmlEndpointsEnum {
	QUOTATION = "quotation",
	DELIVERY_NOTE = "delivery_note",
}

interface ExportXmlProps {
	documentProps: CompleteJobDocumentInformation;
	patient: PatientEntityType;
	endpoint: XmlEndpointsEnum;
	fileName?: string;
}

export interface ExportXmlResult {
	success: boolean;
	error: string;
	/** Raw Sumex/service failure text, shown as toast description and sent to support. */
	errorDetail?: string;
	/** Prefilled Intercom message, set whenever the failure is worth reporting. */
	supportMessage?: string;
	/** Missing fields found before the request; drives red borders and focus. */
	problems?: PreflightProblem[];
}

export const useExportXml = () => {
	const organization = useCentralStore((state) => state.organization);

	const exportXml = async ({
		documentProps,
		patient,
		endpoint,
		fileName,
	}: ExportXmlProps): Promise<ExportXmlResult> => {
		if (!organization) {
			Logger.error("Organization not found", {}, organization);
			return {
				success: false,
				error: "Die Organisation konnte nicht gefunden werden.",
			};
		}

		const version = resolveSumexXmlVersion(
			documentProps.client?.sumex_xml_version
		);
		const jobCode = documentProps.job?.code;

		try {
			const { data, error, problems } = formatDocumentPropsForApi({
				documentProps: {
					...documentProps,
					patient,
					organization,
				},
				documentType: documentProps.jobDocument.type as
					| JobDocumentTypeEnum.DeliveryNote
					| JobDocumentTypeEnum.Quotation,
			});
			if (!data) {
				// Preflight: the export never leaves the browser when a field the Sumex
				// module requires is missing. Problems drive red borders and focus.
				return {
					success: false,
					error: error ?? "Die Dokumentdaten sind unvollständig.",
					problems,
				};
			}

			const rawXml = await postDataToApi({
				endpoint,
				data,
			});
			const xml = removeStringTag(rawXml.data);

			const finalFileName = fileName ?? uuid();
			const xmlTemplateType = getXmlTemplateType(
				documentProps.jobDocument.type as JobDocumentTypeEnum | null
			);
			const organizationId = organization.id;
			if (!organizationId) {
				Logger.error("Organization ID not found", {}, organization);
				return {
					success: false,
					error: "Organization ID not found",
				};
			}
			const { data: dbData, error: dbError } = await supabase
				.from(SupabaseTableEnum.FILES)
				.insert([
					{
						file_name: `${finalFileName}.xml`,
						bucket_name: StorageBucketsEnum.V1,
						path_name: `${
							organization.id
						}/${uuid()}/${finalFileName}.xml`,
						job_document_id: documentProps.jobDocument.id,
						job_id: documentProps.jobDocument.job_id,
						organization_id: organizationId,
						meta_data: createFileMetadata(
							{
								name: `${finalFileName}.xml`,
								type: "xml",
							},
							xmlTemplateType ?? undefined,
							data.xmlVersion
						),
					},
				])
				.select()
				.single();

			if (!dbData || dbError || !dbData["path_name"]) {
				Logger.error(dbError, {}, "Error saving file to database");
				return {
					success: false,
					error: "Error saving file to database",
				};
			}

			const { data: storageData, error: storageError } =
				await supabase.storage
					.from(StorageBucketsEnum.V1)
					.upload(dbData.path_name, xml, {
						contentType: "application/xml",
					});

			Logger.info("File saved to database and storage", {
				storageData,
				storageError,
				dbData,
				dbError,
			});

			return {
				success: true,
				error: "",
			};
		} catch (e) {
			Logger.error(e);

			const detail = extractServiceErrorDetail(e);
			const mapped = mapSumexError(detail, {
				version,
				isTransportFailure: e instanceof AxiosError && !e.response,
			});

			return {
				success: false,
				error: mapped.message,
				errorDetail: mapped.detail,
				problems: mapped.wireField
					? [problemForWireField(mapped.wireField, mapped.message)]
					: undefined,
				supportMessage: buildSumexSupportMessage({
					detail: mapped.detail,
					version,
					endpoint,
					jobCode,
				}),
			};
		}
	};

	return {
		exportXml,
	};
};
